import { CommanderActionTypesEnum } from '@spazious/dollhouse-commander';
import {
  convertNodeToShallow,
  FCompositionNode,
  FInstanceEnsembleNode,
  FNode,
  FNodeTypesEnum,
  isFArrangementNode,
  isFEnsembleNode,
  nodePositionHash,
} from '@spazious/dollhouse-fsetup';
import { FCompositionType } from '@spazious/dollhouse-graphql';
import { useGetFCompositionsSuspenseQuery } from '@spazious/dollhouse-graphql/apollo';
import {
  FCompositionTypesEnum,
  FShallowNode,
  FShallowNodeTypesEnum,
  GraphAction,
} from '@spazious/dollhouse-shallow-graph';
import { useGraph, useStaging } from '@spazious/dollhouse-store';
import { pointRotate } from '@spazious/dollhouse-three';

import { compositionSchemaType } from '../utils';

export const useUnlinkNode = () => {
  const spaceId = useGraph().graph.shallow.space;
  const { createAction, staging, getShallow } = useStaging();
  const { graph } = useGraph();
  const { data: { fcompositions: availableEnsembles = [] } = {} } =
    useGetFCompositionsSuspenseQuery({
      skip: !spaceId,
      variables: { spaceId: `${spaceId}`!, type: FCompositionType.Ensemble },
    });

  const instanceEnsembleToShallowNode = (
    node: FInstanceEnsembleNode,
    nid: number,
    originator: FCompositionNode
  ): FShallowNode => {
    const nodePosition = node.position ?? [0, 0];
    const rotation = (node.rotation ?? 0) + originator.rotation;
    const position = pointRotate(
      [nodePosition[0] + originator.position[0], nodePosition[1] + originator.position[1]],
      -originator.rotation,
      originator.position
    );

    return {
      ...node,
      id: nid,
      type: FShallowNodeTypesEnum.FComposition,
      position,
      rotation,
    };
  };

  const cloneNode = (node: FNode, originator: FCompositionNode): FShallowNode => {
    // eslint-disable-next-line no-unused-vars
    const { selector: _, ...nodeProps } = convertNodeToShallow(node) as any;
    // eslint-disable-next-line no-unused-vars
    const { type: __, ...overrideProps } = { ...getShallow(node.selector!) } as any;

    const position = pointRotate(
      [node.position[0] + originator.position[0], node.position[1] + originator.position[1]],
      -originator.rotation,
      originator.position
    );

    return {
      ...nodeProps,
      ...overrideProps,
      position,
      rotation: node.rotation + originator.rotation,
    };
  };

  const unlinkComposition = (originator: FCompositionNode) => {
    const meta: Record<string, any> = {};

    originator.traverse((node) => {
      if (node.type === FNodeTypesEnum.Furniture && node.visible !== false) {
        if (node.meta) {
          const key = nodePositionHash(node);
          meta[key] = node.meta;
        }
      }
    });

    const fnodes = new Set<FNode>();
    const shallowNodes = new Set<FShallowNode>();
    let unlinkFurnitures = true;

    if (isFArrangementNode(originator) && originator.instance.ensembles.length > 0) {
      // check if we have the corresponding ensemble schema
      const { ensembles } = originator.instance;
      const schemaType = compositionSchemaType(FCompositionTypesEnum.FEnsemble, ensembles[0].type);
      const schema = availableEnsembles.find((s) => s.name === schemaType);
      if (schema) {
        unlinkFurnitures = false;
        ensembles.forEach((node) => {
          const newNode = instanceEnsembleToShallowNode(node, schema.nid, originator);
          shallowNodes.add(newNode);
        });
      }
    }

    // if not unlinked by ensembles, separate each furniture
    if (unlinkFurnitures) {
      originator.traverse((node) => {
        if (node.type === FNodeTypesEnum.Furniture && node.visible !== false) {
          fnodes.add(node);
        }
      });

      fnodes.forEach((node) => {
        const newNode = cloneNode(node, originator);
        shallowNodes.add(newNode);
      });
    }

    return { shallowNodes, meta };
  };

  const unlink = (compositions: FCompositionNode[]) => {
    staging.prepare();

    let metaRecords: Record<string, any> = {};

    // create clone of all visible furnitures in composition
    // and then remove the original composition
    compositions.forEach((originator) => {
      const { shallowNodes, meta } = unlinkComposition(originator);
      metaRecords = { ...metaRecords, ...meta };
      shallowNodes.forEach((node) => {
        staging.add(createAction('add', node));
      });

      staging.add(createAction('remove', originator.selector!));
    });

    // TODO - find a better way to detect when all furnitures in scene are rendered
    // wait for the furnitures to be rendered, adn then update their metadata
    // metadata is amapped based on the position of the furnitures
    const metaHandler = () => {
      const updateActions: GraphAction[] = [];
      graph.traverse((node) => {
        if (node.type === FNodeTypesEnum.Furniture) {
          const originator = node.originator;
          if (originator && isFEnsembleNode(originator)) {
            const key = nodePositionHash(node);
            if (metaRecords[key]) {
              updateActions.push(
                createAction('update', node.selector!, 'meta', { ...metaRecords[key] })
              );
              delete metaRecords[key];
            }
          }
        }
      });
      if (updateActions.length > 0) {
        staging.add(updateActions);
      }
      if (Object.keys(metaRecords).length === 0) {
        graph.off('update', metaHandler);
        staging.commit(CommanderActionTypesEnum.Update);
      }
    };

    graph.on('update', metaHandler);
  };

  return { unlink };
};
